import { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// ── Data ───────────────────────────────────────────────────────────────────────

const TICKERS = [
  { sym: "BTC",   price: "$67,420", change: "+2.4%",  up: true  },
  { sym: "ETH",   price: "$3,891",  change: "+1.8%",  up: true  },
  { sym: "SOL",   price: "$184.60", change: "+5.2%",  up: true  },
  { sym: "BNB",   price: "$612.30", change: "-0.4%",  up: false },
  { sym: "ADA",   price: "$0.724",  change: "+3.1%",  up: true  },
  { sym: "AVAX",  price: "$38.64",  change: "+2.9%",  up: true  },
  { sym: "DOT",   price: "$9.84",   change: "-1.2%",  up: false },
  { sym: "MATIC", price: "$1.121",  change: "+4.3%",  up: true  },
  { sym: "LINK",  price: "$18.72",  change: "+1.6%",  up: true  },
  { sym: "XRP",   price: "$0.621",  change: "+0.8%",  up: true  },
];

// Orbiting tokens — each defines orbit radius, speed, initial angle, color
const ORBIT_TOKENS = [
  { sym: "ETH",  color: "#627EEA", bg: "rgba(98,126,234,0.12)",  r: 175, dur: "22s", deg: 0   },
  { sym: "SOL",  color: "#9945FF", bg: "rgba(153,69,255,0.12)",  r: 230, dur: "30s", deg: 130 },
  { sym: "BNB",  color: "#F3BA2F", bg: "rgba(243,186,47,0.12)",  r: 148, dur: "17s", deg: 250 },
  { sym: "ADA",  color: "#0033AD", bg: "rgba(0,51,173,0.15)",    r: 260, dur: "38s", deg: 60  },
];

// Blockchain graph node positions (as % of container)
const NODES = [
  { x: 12, y: 18 }, { x: 85, y: 14 }, { x: 6,  y: 72 },
  { x: 92, y: 68 }, { x: 48, y: 8  }, { x: 50, y: 90 },
  { x: 20, y: 48 }, { x: 80, y: 45 },
];
const EDGES = [
  [0,4],[4,1],[1,7],[7,3],[3,5],[5,6],[6,0],[4,7],[0,6],[1,3],[2,5],[2,6],
];

// ── Sub-components ─────────────────────────────────────────────────────────────

function BlockchainBg() {
  return (
    <svg
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
      preserveAspectRatio="none"
      viewBox="0 0 100 100"
    >
      <defs>
        <linearGradient id="edgeGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#10B77F" stopOpacity="0" />
          <stop offset="50%"  stopColor="#10B77F" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#10B77F" stopOpacity="0" />
        </linearGradient>
        <filter id="nodeGlow">
          <feGaussianBlur stdDeviation="0.5" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      {EDGES.map(([a, b], i) => (
        <line
          key={i}
          x1={NODES[a].x} y1={NODES[a].y}
          x2={NODES[b].x} y2={NODES[b].y}
          stroke="url(#edgeGrad)"
          strokeWidth="0.18"
          className={`bc-edge bc-edge-${i}`}
        />
      ))}
      {NODES.map((n, i) => (
        <g key={i} filter="url(#nodeGlow)">
          <circle cx={n.x} cy={n.y} r="0.9" fill="#10B77F" fillOpacity="0.18" />
          <circle cx={n.x} cy={n.y} r="0.45" fill="#10B77F" fillOpacity="0.6" className={`bc-node bc-node-${i}`} />
        </g>
      ))}
    </svg>
  );
}

function CoinFace() {
  return (
    <svg viewBox="0 0 200 200" style={{ width: "100%", height: "100%" }}>
      <defs>
        <radialGradient id="coinFaceGrad" cx="38%" cy="35%">
          <stop offset="0%"   stopColor="#1EE09A" />
          <stop offset="55%"  stopColor="#10B77F" />
          <stop offset="100%" stopColor="#065F46" />
        </radialGradient>
        <radialGradient id="coinShine" cx="30%" cy="28%">
          <stop offset="0%"   stopColor="rgba(255,255,255,0.35)" />
          <stop offset="60%"  stopColor="rgba(255,255,255,0)" />
        </radialGradient>
        <filter id="coinShadow">
          <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="#10B77F" floodOpacity="0.5" />
        </filter>
        <clipPath id="coinClip">
          <circle cx="100" cy="100" r="96" />
        </clipPath>
      </defs>

      {/* Outer ring */}
      <circle cx="100" cy="100" r="98"
        fill="none"
        stroke="url(#coinFaceGrad)"
        strokeWidth="1.5"
        opacity="0.6"
      />
      {/* Coin body */}
      <circle cx="100" cy="100" r="96"
        fill="url(#coinFaceGrad)"
        filter="url(#coinShadow)"
      />
      {/* Inner bevel */}
      <circle cx="100" cy="100" r="88"
        fill="none"
        stroke="rgba(255,255,255,0.08)"
        strokeWidth="1"
      />
      {/* Shine highlight */}
      <circle cx="100" cy="100" r="96" fill="url(#coinShine)" clipPath="url(#coinClip)" />

      {/* BTC ₿ symbol */}
      <text
        x="100" y="118"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontWeight="900"
        fontSize="72"
        fill="rgba(255,255,255,0.92)"
        style={{ letterSpacing: "-2px" }}
      >₿</text>

      {/* Tick marks around edge */}
      {Array.from({ length: 32 }, (_, i) => {
        const angle = (i / 32) * Math.PI * 2;
        const r1 = 90, r2 = i % 4 === 0 ? 83 : 86;
        return (
          <line
            key={i}
            x1={100 + Math.cos(angle) * r1}
            y1={100 + Math.sin(angle) * r1}
            x2={100 + Math.cos(angle) * r2}
            y2={100 + Math.sin(angle) * r2}
            stroke="rgba(255,255,255,0.15)"
            strokeWidth={i % 4 === 0 ? 1.2 : 0.7}
          />
        );
      })}
    </svg>
  );
}

function OrbitToken({ sym, color, bg, r, dur, deg }) {
  return (
    <div style={{
      position: "absolute",
      top: "50%", left: "50%",
      width: r * 2, height: r * 2,
      marginTop: -r, marginLeft: -r,
      borderRadius: "50%",
      border: `1px solid ${color}20`,
      animation: `orbitSpin ${dur} linear infinite`,
      transform: `rotate(${deg}deg)`,
    }}>
      {/* Token badge — counter-rotates to stay upright */}
      <div style={{
        position: "absolute",
        top: -18, left: "50%", marginLeft: -18,
        width: 36, height: 36,
        borderRadius: "50%",
        background: bg,
        border: `1.5px solid ${color}55`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "0.7rem", fontWeight: 800, color,
        fontFamily: "Space Grotesk, sans-serif",
        boxShadow: `0 0 18px ${color}40, 0 0 6px ${color}30`,
        animation: `orbitCounter ${dur} linear infinite`,
        transform: `rotate(-${deg}deg)`,
        backdropFilter: "blur(8px)",
      }}>{sym}</div>
    </div>
  );
}

function TickerBar() {
  const items = [...TICKERS, ...TICKERS]; // double for seamless loop
  return (
    <div style={{
      overflow: "hidden",
      borderTop: "1px solid rgba(255,255,255,0.06)",
      borderBottom: "1px solid rgba(255,255,255,0.06)",
      background: "rgba(0,0,0,0.3)",
      padding: "10px 0",
    }}>
      <div style={{
        display: "flex",
        gap: 0,
        animation: "tickerScroll 28s linear infinite",
        width: "max-content",
      }}>
        {items.map((t, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "0 28px",
            borderRight: "1px solid rgba(255,255,255,0.05)",
            whiteSpace: "nowrap",
          }}>
            <span style={{
              fontSize: "0.72rem", fontWeight: 800,
              color: "rgba(255,255,255,0.65)",
              fontFamily: "Space Grotesk, sans-serif",
              letterSpacing: "0.04em",
            }}>{t.sym}</span>
            <span style={{
              fontSize: "0.78rem", fontWeight: 700,
              color: "#F0EEF6",
              fontFamily: "Space Grotesk, sans-serif",
            }}>{t.price}</span>
            <span style={{
              fontSize: "0.68rem", fontWeight: 700,
              color: t.up ? "#10B77F" : "#F87171",
              fontFamily: "Space Grotesk, sans-serif",
            }}>{t.change}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function CryptoSection() {
  const sectionRef  = useRef(null);
  const coinRef     = useRef(null);
  const orbitsRef   = useRef(null);
  const textRef     = useRef(null);
  const card1Ref    = useRef(null);
  const card2Ref    = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {

      // ① Coin: scale + rotateY entrance from scroll
      gsap.fromTo(coinRef.current,
        { scale: 0.55, rotateY: -55, opacity: 0 },
        {
          scale: 1, rotateY: 0, opacity: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 75%",
            end: "top 20%",
            scrub: 1.2,
          },
        }
      );

      // ② Coin: subtle continued tilt + scale as user scrolls deeper
      gsap.fromTo(coinRef.current,
        { rotateX: 0, rotateY: 0 },
        {
          rotateX: 12, rotateY: 18,
          ease: "sine.inOut",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 20%",
            end: "bottom top",
            scrub: 2,
          },
        }
      );

      // ③ Orbits ring: scale in from 0
      gsap.fromTo(orbitsRef.current,
        { scale: 0, opacity: 0 },
        {
          scale: 1, opacity: 1,
          ease: "back.out(1.4)",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 70%",
            end: "top 30%",
            scrub: 1,
          },
        }
      );

      // ④ Text slides up
      gsap.fromTo(textRef.current,
        { y: 50, opacity: 0 },
        {
          y: 0, opacity: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
            end: "top 40%",
            scrub: 0.8,
          },
        }
      );

      // ⑤ Floating price cards parallax
      if (card1Ref.current) {
        gsap.fromTo(card1Ref.current,
          { y: 40, opacity: 0, x: -20 },
          {
            y: -30, opacity: 1, x: 0,
            ease: "none",
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 65%",
              end: "bottom top",
              scrub: 1.2,
            },
          }
        );
      }
      if (card2Ref.current) {
        gsap.fromTo(card2Ref.current,
          { y: 60, opacity: 0, x: 20 },
          {
            y: -50, opacity: 1, x: 0,
            ease: "none",
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 65%",
              end: "bottom top",
              scrub: 1.6,
            },
          }
        );
      }
    });
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      style={{
        position: "relative",
        padding: "100px 24px 0",
        overflow: "hidden",
      }}
    >
      {/* Blockchain network background */}
      <div style={{ position: "absolute", inset: 0, opacity: 0.55, pointerEvents: "none" }}>
        <BlockchainBg />
      </div>

      {/* Radial glow */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background:
          "radial-gradient(ellipse 70% 55% at 60% 50%, rgba(16,183,127,0.07) 0%, transparent 65%)," +
          "radial-gradient(ellipse 40% 35% at 65% 50%, rgba(98,126,234,0.06) 0%, transparent 60%)",
      }} />

      <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1 }}>

        {/* Section label */}
        <div ref={textRef} style={{ textAlign: "center", marginBottom: 48 }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            fontSize: "0.67rem", fontWeight: 700, letterSpacing: "0.15em",
            textTransform: "uppercase", color: "#10B77F",
            fontFamily: "Space Grotesk, sans-serif",
            marginBottom: 20,
          }}>
            <span style={{ width: 20, height: 1, background: "#10B77F", opacity: 0.5 }} />
            Digital Asset Intelligence
            <span style={{ width: 20, height: 1, background: "#10B77F", opacity: 0.5 }} />
          </div>

          <h2 style={{
            fontFamily: "Space Grotesk, sans-serif",
            fontSize: "clamp(2rem, 4vw, 3.2rem)",
            fontWeight: 800, lineHeight: 1.1, letterSpacing: "-0.03em",
            color: "#F0EEF6", marginBottom: 16,
          }}>
            Track every digital asset.<br />
            <span style={{
              background: "linear-gradient(135deg, #10B77F 0%, #6EE7B7 50%, #10B77F 100%)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>In real time.</span>
          </h2>

          <p style={{
            fontSize: "1rem", color: "rgba(240,238,246,0.48)",
            lineHeight: 1.75, maxWidth: 520, margin: "0 auto",
            fontFamily: "Space Grotesk, sans-serif",
          }}>
            Alpha Stack monitors 500+ digital assets 24/7 — price action, on-chain signals,
            and macro correlations — so you never miss a move that matters.
          </p>
        </div>

        {/* Coin + orbits visual */}
        <div style={{ position: "relative", height: 540, display: "flex", alignItems: "center", justifyContent: "center" }}>

          {/* Orbit rings + tokens */}
          <div ref={orbitsRef} style={{
            position: "absolute",
            top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            width: 560, height: 560,
          }}>
            {ORBIT_TOKENS.map((t) => (
              <OrbitToken key={t.sym} {...t} />
            ))}

            {/* Outer glow ring */}
            <div style={{
              position: "absolute", top: "50%", left: "50%",
              width: 520, height: 520,
              marginTop: -260, marginLeft: -260,
              borderRadius: "50%",
              border: "1px solid rgba(16,183,127,0.06)",
              boxShadow: "0 0 60px rgba(16,183,127,0.04) inset",
            }} />
          </div>

          {/* Main 3D coin */}
          <div style={{ perspective: "1200px", perspectiveOrigin: "50% 50%", position: "relative", zIndex: 5 }}>
            <div ref={coinRef} style={{
              width: 200, height: 200,
              transformStyle: "preserve-3d",
              animation: "coinFloat 6s ease-in-out infinite",
              filter: "drop-shadow(0 30px 60px rgba(16,183,127,0.45)) drop-shadow(0 0 30px rgba(16,183,127,0.3))",
            }}>
              <CoinFace />
            </div>

            {/* Reflection / glow beneath */}
            <div style={{
              position: "absolute", bottom: -40, left: "50%",
              transform: "translateX(-50%)",
              width: 160, height: 30,
              background: "radial-gradient(ellipse, rgba(16,183,127,0.4) 0%, transparent 70%)",
              filter: "blur(16px)",
              animation: "coinFloat 6s ease-in-out infinite reverse",
            }} />
          </div>

          {/* Floating price card 1 — top left */}
          <div ref={card1Ref} style={{
            position: "absolute", top: 40, left: "calc(50% - 290px)",
            background: "rgba(8,10,18,0.9)",
            border: "1px solid rgba(16,183,127,0.2)",
            borderRadius: 14, padding: "12px 16px",
            backdropFilter: "blur(20px)",
            boxShadow: "0 20px 50px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)",
            display: "flex", alignItems: "center", gap: 12,
            whiteSpace: "nowrap",
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: "rgba(247,147,26,0.12)", border: "1px solid rgba(247,147,26,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "1.1rem",
            }}>₿</div>
            <div>
              <div style={{ fontSize: "0.6rem", color: "rgba(255,255,255,0.3)", fontFamily: "Space Grotesk, sans-serif", marginBottom: 2 }}>Bitcoin</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: "0.92rem", fontWeight: 800, color: "#F0EEF6", fontFamily: "Space Grotesk, sans-serif" }}>$67,420</span>
                <span style={{ fontSize: "0.68rem", fontWeight: 700, color: "#10B77F", background: "rgba(16,183,127,0.1)", border: "1px solid rgba(16,183,127,0.2)", padding: "2px 7px", borderRadius: 6 }}>+2.4%</span>
              </div>
            </div>
          </div>

          {/* Floating price card 2 — bottom right */}
          <div ref={card2Ref} style={{
            position: "absolute", bottom: 60, right: "calc(50% - 310px)",
            background: "rgba(8,10,18,0.9)",
            border: "1px solid rgba(98,126,234,0.2)",
            borderRadius: 14, padding: "12px 16px",
            backdropFilter: "blur(20px)",
            boxShadow: "0 20px 50px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04)",
            display: "flex", alignItems: "center", gap: 12,
            whiteSpace: "nowrap",
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: "rgba(98,126,234,0.12)", border: "1px solid rgba(98,126,234,0.22)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "1.1rem", color: "#627EEA", fontWeight: 800,
              fontFamily: "Space Grotesk, sans-serif",
            }}>Ξ</div>
            <div>
              <div style={{ fontSize: "0.6rem", color: "rgba(255,255,255,0.3)", fontFamily: "Space Grotesk, sans-serif", marginBottom: 2 }}>Ethereum</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: "0.92rem", fontWeight: 800, color: "#F0EEF6", fontFamily: "Space Grotesk, sans-serif" }}>$3,891</span>
                <span style={{ fontSize: "0.68rem", fontWeight: 700, color: "#10B77F", background: "rgba(16,183,127,0.1)", border: "1px solid rgba(16,183,127,0.2)", padding: "2px 7px", borderRadius: 6 }}>+1.8%</span>
              </div>
            </div>
          </div>

          {/* On-chain signal badge */}
          <div style={{
            position: "absolute", top: 70, right: "calc(50% - 300px)",
            background: "rgba(8,10,18,0.88)",
            border: "1px solid rgba(167,139,250,0.22)",
            borderRadius: 12, padding: "9px 14px",
            backdropFilter: "blur(16px)",
            display: "flex", alignItems: "center", gap: 8,
            whiteSpace: "nowrap",
            animation: "floatCard 5.5s ease-in-out 1.2s infinite",
          }}>
            <div style={{
              width: 8, height: 8, borderRadius: "50%",
              background: "#A78BFA",
              animation: "pulseGold 1.6s ease-in-out infinite",
            }} />
            <span style={{ fontSize: "0.68rem", fontWeight: 700, color: "rgba(167,139,250,0.9)", fontFamily: "Space Grotesk, sans-serif" }}>
              94 On-chain signals · Live
            </span>
          </div>

        </div>
      </div>

      {/* Ticker strip */}
      <div style={{ marginTop: 48 }}>
        <TickerBar />
      </div>

    </section>
  );
}
